import { useEffect, useMemo, useState } from 'react'
import { View, Text, TextInput, TouchableOpacity, StyleSheet, KeyboardAvoidingView, Platform, ScrollView, Image, Linking, Modal, Dimensions } from 'react-native'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import * as AppleAuthentication from 'expo-apple-authentication'
import Ionicons from '@expo/vector-icons/Ionicons'
import { signIn, signUp } from '../lib/auth'
import { supabase } from '../lib/supabase'
import { colors, fonts, radius, spacing, base } from '../lib/theme'
import { useThemeColors } from '../lib/useThemeColors'
import { getAppOrigin } from '../lib/platform'
import { useAuth } from '../lib/AuthContext'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { LOGO } from '../lib/assets'

const LANGUAGES = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'no', label: 'Norsk', flag: '🇳🇴' },
  { code: 'sl', label: 'Slovenščina', flag: '🇸🇮' },
]

function LangPicker({ tc }) {
  const insets = useSafeAreaInsets()
  const [open, setOpen] = useState(false)
  const currentCode = i18n.language?.substring(0, 2) ?? 'it'
  const [selected, setSelected] = useState(currentCode)
  const currentLang = LANGUAGES.find(l => l.code === selected) ?? LANGUAGES[0]

  const handleSelect = (code) => {
    setSelected(code)
    i18n.changeLanguage(code)
    setOpen(false)
  }

  return (
    <>
      {Platform.OS !== 'web' && (
        <Modal visible={open} transparent animationType="slide" onRequestClose={() => setOpen(false)}>
          <View style={{ flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.55)' }}>
            <TouchableOpacity style={StyleSheet.absoluteFillObject} activeOpacity={1} onPress={() => setOpen(false)} />
            <View style={{ backgroundColor: tc.bgCard, borderTopLeftRadius: 20, borderTopRightRadius: 20, borderWidth: 1, borderColor: tc.border }}>
              <View style={{ width: 36, height: 4, backgroundColor: 'rgba(150,150,150,0.4)', borderRadius: 2, alignSelf: 'center', marginTop: 10, marginBottom: 4 }} />
              <Text style={{ fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 1, padding: 14, color: tc.textMuted, borderBottomWidth: 1, borderBottomColor: tc.border, textAlign: 'center' }}>
                Seleziona lingua / Select language
              </Text>
              <ScrollView
                bounces={false}
                style={{ maxHeight: Dimensions.get('window').height * 0.6 }}
                contentContainerStyle={{ paddingBottom: insets.bottom + 32 }}
              >
                {LANGUAGES.map((lang, i) => (
                  <TouchableOpacity
                    key={lang.code}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, paddingHorizontal: 16, borderBottomWidth: i === LANGUAGES.length - 1 ? 0 : 1, borderBottomColor: tc.border, backgroundColor: lang.code === selected ? tc.rosaSubtle : 'transparent' }}
                    onPress={() => handleSelect(lang.code)}
                  >
                    <Text style={{ fontSize: 22 }}>{lang.flag}</Text>
                    <Text style={{ flex: 1, fontSize: 15, fontWeight: '500', color: lang.code === selected ? tc.rosaText : tc.text }}>{lang.label}</Text>
                    {lang.code === selected && <Ionicons name="checkmark" size={16} color={tc.rosaText} />}
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}

      <TouchableOpacity
        onPress={() => setOpen(o => !o)}
        style={{ flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'center', marginTop: 12, marginBottom: 4, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, borderWidth: 1, borderColor: tc.border, backgroundColor: tc.bgCard2 }}
      >
        <Text style={{ fontSize: 16 }}>{currentLang.flag}</Text>
        <Text style={{ fontSize: 13, fontWeight: '600', color: tc.text }}>{currentLang.label}</Text>
        <Ionicons name="chevron-down" size={13} color={tc.textMuted} />
      </TouchableOpacity>

      {/* Web: lista inline */}
      {Platform.OS === 'web' && open && (
        <View style={{ borderWidth: 1, borderColor: tc.border, borderRadius: 12, overflow: 'hidden', marginTop: 4, marginBottom: 8, backgroundColor: tc.bgCard2 }}>
          {LANGUAGES.map((lang, i) => (
            <TouchableOpacity
              key={lang.code}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, paddingHorizontal: 16, borderBottomWidth: i === LANGUAGES.length - 1 ? 0 : 1, borderBottomColor: tc.border, backgroundColor: lang.code === selected ? tc.rosaSubtle : 'transparent' }}
              onPress={() => handleSelect(lang.code)}
            >
              <Text style={{ fontSize: 20 }}>{lang.flag}</Text>
              <Text style={{ flex: 1, fontSize: 14, fontWeight: '500', color: lang.code === selected ? tc.rosaText : tc.text }}>{lang.label}</Text>
              {lang.code === selected && <Ionicons name="checkmark" size={14} color={tc.rosaText} />}
            </TouchableOpacity>
          ))}
        </View>
      )}
    </>
  )
}

function Alert({ type, children }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const isError = type === 'error'
  return (
    <View style={[styles.alert, isError ? styles.alertError : styles.alertSuccess]}>
      <Text style={[styles.alertText, isError ? styles.alertErrorText : styles.alertSuccessText]}>
        {children}
      </Text>
    </View>
  )
}

export default function Login() {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { t } = useTranslation()
  const router = useRouter()
  const params = useLocalSearchParams()
  const redirectTo = params.redirect || '/'
  const { isLoggedIn } = useAuth()

  const [tab, setTab] = useState('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [nickname, setNickname] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const [showForgot, setShowForgot] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')

  useEffect(() => {
    if (isLoggedIn) router.replace(redirectTo)
  }, [isLoggedIn])

  const handleLogin = async () => {
    setError(''); setLoading(true)
    try {
      await signIn(email, password)
      router.replace(redirectTo)
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }

  const handleRegister = async () => {
    if (!nickname) { setError(t('login.err_nickname')); return }
    setError(''); setSuccess(''); setLoading(true)
    try {
      const { data: available } = await supabase.rpc('fn_check_nickname', { p_nickname: nickname })
      if (!available) { setError(t('login.err_nickname_taken')); setLoading(false); return }
      await signUp(email, password, nickname)
      setSuccess(t('login.register_ok'))
      setTab('login')
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }

  const handleGoogleLogin = async () => {
    setError('')
    const redirect = Platform.OS === 'web'
      ? getAppOrigin() + redirectTo
      : 'virtuacycling://'
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: redirect },
    })
    if (error) { setError(error.message); return }
    if (Platform.OS !== 'web' && data?.url) Linking.openURL(data.url)
  }

  const handleAppleLogin = async () => {
    setError('')
    try {
      const credential = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
      })
      const { error } = await supabase.auth.signInWithIdToken({
        provider: 'apple',
        token: credential.identityToken,
      })
      if (error) throw error
      router.replace(redirectTo)
    } catch (e) {
      if (e.code === 'ERR_REQUEST_CANCELED') return
      setError(e.message)
    }
  }

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) { setError(t('login.err_email')); return }
    setError(''); setLoading(true)
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
        redirectTo: `${getAppOrigin()}/reset-password`,
      })
      if (error) throw error
      setSuccess(t('login.forgot_sent'))
      setShowForgot(false)
      setForgotEmail('')
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }

  return (
    <KeyboardAvoidingView style={[styles.container, { backgroundColor: tc.bg }]} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">

        {/* Header */}
        <View style={styles.header}>
          <Image
            source={LOGO}
            style={styles.logo}
            resizeMode="contain"
          />
          <Text style={styles.brandTitle}>VIRTUA CYCLING</Text>
          <Text style={styles.tagline}>{t('login.tagline')}</Text>
          <LangPicker tc={tc} />
        </View>

        {/* Tab switcher */}
        {!showForgot && (
          <View style={styles.tabs}>
            {[
              { key: 'login', icon: 'lock-closed-outline', label: t('login.tab_login') },
              { key: 'register', icon: 'person-add-outline', label: t('login.tab_register') },
            ].map(({ key, icon, label }) => (
              <TouchableOpacity
                key={key}
                onPress={() => { setTab(key); setError(''); setSuccess(''); setShowForgot(false) }}
                style={[styles.tabBtn, tab === key && styles.tabBtnActive]}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Ionicons name={icon} size={13} color={tab === key ? tc.brandText : tc.textMuted} />
                  <Text style={[styles.tabText, tab === key && styles.tabTextActive]}>{label}</Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {error ? <Alert type="error">{error}</Alert> : null}
        {success ? <Alert type="success">{success}</Alert> : null}

        {/* Form recupero password */}
        {showForgot ? (
          <View style={styles.form}>
            <Text style={styles.hint}>{t('login.forgot_hint')}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('login.email')}
              placeholderTextColor={tc.textDim}
              value={forgotEmail}
              onChangeText={setForgotEmail}
              keyboardType="email-address"
              autoCapitalize="none"
            />
            <TouchableOpacity style={[styles.btn, loading && styles.btnDisabled]} onPress={handleForgotPassword} disabled={loading}>
              <Text style={styles.btnText}>{loading ? t('login.loading') : t('login.forgot_send')}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => { setShowForgot(false); setError('') }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 8 }}>
                <Ionicons name="arrow-back-outline" size={13} color={tc.textMuted} />
                <Text style={styles.link}>{t('login.back')}</Text>
              </View>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.form}>
            {tab === 'register' && (
              <TextInput
                style={styles.input}
                placeholder={t('login.nickname')}
                placeholderTextColor={tc.textDim}
                value={nickname}
                onChangeText={setNickname}
                autoCapitalize="none"
              />
            )}
            <TextInput
              style={styles.input}
              placeholder={t('login.email')}
              placeholderTextColor={tc.textDim}
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
            />

            {/* Password con occhietto mostra/nascondi */}
            <View style={styles.passwordWrap}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                placeholder={t('login.password')}
                placeholderTextColor={tc.textDim}
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
                autoCapitalize="none"
              />
              <TouchableOpacity
                style={styles.eyeBtn}
                onPress={() => setShowPassword(s => !s)}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons
                  name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                  size={18}
                  color={tc.textMuted}
                />
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={[styles.btn, loading && styles.btnDisabled]}
              onPress={tab === 'login' ? handleLogin : handleRegister}
              disabled={loading}
            >
              <Text style={styles.btnText}>
                {loading ? t('login.loading') : tab === 'login' ? t('login.btn_login') : t('login.btn_register')}
              </Text>
            </TouchableOpacity>

            {tab === 'login' && (
              <TouchableOpacity onPress={() => { setShowForgot(true); setError(''); setSuccess(''); setForgotEmail(email) }}>
                <Text style={styles.link}>{t('login.forgot_pass')}</Text>
              </TouchableOpacity>
            )}

            {/* Divider */}
            <View style={styles.divider}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>{t('login.or')}</Text>
              <View style={styles.dividerLine} />
            </View>

            {/* Google */}
            <TouchableOpacity style={styles.googleBtn} onPress={handleGoogleLogin}>
              <Image
                source={{ uri: 'https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png' }}
                style={styles.googleIcon}
              />
              <Text style={styles.googleText}>
                {tab === 'login' ? t('login.google_login') : t('login.google_register')}
              </Text>
            </TouchableOpacity>

            {/* Apple — solo su iOS */}
            {Platform.OS === 'ios' && (
              <AppleAuthentication.AppleAuthenticationButton
                buttonType={tab === 'login'
                  ? AppleAuthentication.AppleAuthenticationButtonType.SIGN_IN
                  : AppleAuthentication.AppleAuthenticationButtonType.SIGN_UP}
                buttonStyle={AppleAuthentication.AppleAuthenticationButtonStyle.WHITE_OUTLINE}
                cornerRadius={10}
                style={styles.appleBtn}
                onPress={handleAppleLogin}
              />
            )}

            {/* Privacy */}
            <Text style={styles.privacy}>
              {t('login.privacy_text')}{' '}
              <Text style={styles.privacyLink} onPress={() => router.push('/privacy')}>{t('login.privacy_link')}</Text>
            </Text>
          </View>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container: { flex: 1 },
  scroll: { flexGrow: 1, justifyContent: 'center', padding: 24 },

  header: { alignItems: 'center', marginBottom: 32 },
  logo: { width: 64, height: 64, marginBottom: 12 },
  brandTitle: { fontFamily: fonts.condensedBold, fontSize: 32, color: tc.text, letterSpacing: 1 },
  tagline: { fontSize: 11, color: tc.textDim, textTransform: 'uppercase', letterSpacing: 2, marginTop: 8 },

  tabs: { flexDirection: 'row', marginBottom: 24, borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: tc.border },
  tabBtn: { flex: 1, padding: 12, alignItems: 'center', justifyContent: 'center' },
  tabBtnActive: { backgroundColor: tc.rosa },
  tabText: { fontFamily: fonts.condensedBold, fontSize: 13, color: tc.textMuted },
  tabTextActive: { color: tc.brandText },

  form: { gap: 12 },
  input: { backgroundColor: tc.bgCard2, borderWidth: 1, borderColor: tc.border, borderRadius: 10, padding: 14, fontSize: 14, color: tc.text },
  passwordWrap: { position: 'relative', justifyContent: 'center' },
  passwordInput: { paddingRight: 44 },
  eyeBtn: { position: 'absolute', right: 12, height: '100%', justifyContent: 'center' },
  btn: { ...base.btnPrimary, marginTop: 4, backgroundColor: tc.rosa },
  btnDisabled: { opacity: 0.6 },
  btnText: { ...base.btnPrimaryText, fontSize: 16, color: tc.brandText },
  link: { color: tc.textMuted, textAlign: 'center', fontSize: 12, textDecorationLine: 'underline' },
  hint: { color: tc.textMuted, fontSize: 13, lineHeight: 20, marginBottom: 8, textAlign: 'center' },

  divider: { flexDirection: 'row', alignItems: 'center', marginVertical: 16 },
  dividerLine: { flex: 1, height: 1, backgroundColor: tc.border },
  dividerText: { color: tc.textDim, fontSize: 11, textTransform: 'uppercase', paddingHorizontal: 10 },

  googleBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: '#747775', borderRadius: 20, padding: 13, backgroundColor: '#FFFFFF', gap: 10 },
  googleIcon: { width: 18, height: 18 },
  googleText: { color: '#1F1F1F', fontWeight: '500', fontSize: 14 },

  appleBtn: { width: '100%', height: 48 },

  privacy: { textAlign: 'center', fontSize: 11, color: tc.textDim, marginTop: 16 },
  privacyLink: { color: tc.rosaText },

  alert: { padding: 12, borderRadius: 8, marginBottom: 16, borderWidth: 1 },
  alertError: { backgroundColor: tc.rosaSubtle, borderColor: tc.rosaBorder },
  alertSuccess: { backgroundColor: 'rgba(234,179,8,0.1)', borderColor: 'rgba(234,179,8,0.3)' },
  alertText: { fontSize: 13, textAlign: 'center' },
  alertErrorText: { color: tc.rosaText },
  alertSuccessText: { color: '#EAB308' },
})